/**
 * AgendaView — what is coming, as a list.
 *
 * CAL-PRO-1. The view people actually live in on a phone, and the one
 * that answers "what's next" without making anyone read a grid. It is
 * deliberately NOT a day picker: it runs forward from today across
 * however many days have something in them, because the question is
 * never "what is on the 14th", it is "what is coming".
 *
 * Empty days are skipped rather than drawn empty. A month of "nothing
 * scheduled" rows is a month of scrolling past nothing.
 *
 * PLANNER-2 — dressed in the planner's tokens. "Today" is the chosen
 * accent rather than a fixed red: in here the accent is already what
 * says you are here, on the now-line and on today's date, and a second
 * colour claiming the same thing would be one too many.
 */
import { useMemo } from 'react';
import { format, isToday, isTomorrow, isSameDay } from 'date-fns';
import { MapPin, Video } from 'lucide-react';
import { OrbitIcon } from '@/components/icons/OrbitIcon';
import { cn } from '@/lib/utils';
import type { DetailedCalendarEvent } from './EventDetailsModal';

/** Today, Tomorrow, then the date — the way people say it. */
function dayLabel(day: Date): string {
  if (isToday(day)) return 'Today';
  if (isTomorrow(day)) return 'Tomorrow';
  return format(day, 'EEEE d MMMM');
}

export interface AgendaViewProps {
  events: DetailedCalendarEvent[];
  /** Per-type colours. The Calendars list owns these. */
  colorOf: (event: DetailedCalendarEvent) => string;
  /** Where the list starts. Defaults to the start of today. */
  from?: Date;
  /** How far forward to look. */
  days?: number;
  onEventClick: (event: DetailedCalendarEvent) => void;
  /** CAL-KEY-1 — the row the keyboard is pointed at. */
  selectedId?: string | null;
  className?: string;
}

export function AgendaView({ events, colorOf, from, days = 60, onEventClick, selectedId, className }: AgendaViewProps) {
  const groups = useMemo(() => {
    const start = from ? new Date(from) : new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + days);

    // An event belongs to the day it STARTS, except one already
    // running, which belongs to today — otherwise a shoot that began
    // yesterday vanishes from the list while you are at it.
    const inRange = events
      .filter((e) => e.endAt >= start && e.startAt < end)
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());

    const out: { day: Date; items: DetailedCalendarEvent[] }[] = [];
    for (const e of inRange) {
      const day = e.startAt < start ? new Date(start) : new Date(e.startAt);
      day.setHours(0, 0, 0, 0);
      const last = out[out.length - 1];
      if (last && isSameDay(last.day, day)) last.items.push(e);
      else out.push({ day, items: [e] });
    }
    return out;
  }, [events, from, days]);

  if (groups.length === 0) {
    return (
      <div className={cn('plan-panel', className)} style={{ padding: '64px 24px', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--muted)' }}>Nothing coming up.</p>
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--muted)', opacity: 0.75 }}>
          Bookings, shoots and project dates all land here.
        </p>
      </div>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {groups.map(({ day, items }) => (
        <section key={day.toISOString()}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '0 4px 8px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: isToday(day) ? 'var(--accent)' : 'var(--fg)',
              }}
            >
              {dayLabel(day)}
            </h3>
            <span className="plan-tnum" style={{ fontSize: 11, color: 'var(--muted)' }}>
              {items.length} {items.length === 1 ? 'thing' : 'things'}
            </span>
          </div>

          <div className="plan-panel" style={{ overflow: 'hidden' }}>
            {items.map((e, i) => (
              <button
                key={e.id}
                type="button"
                onClick={() => onEventClick(e)}
                className={selectedId === e.id ? 'plan-agenda-row sel' : 'plan-agenda-row'}
                style={{ borderTop: i > 0 ? '1px solid var(--line)' : undefined }}
              >
                {/* The time column is fixed width so the titles line
                    up down the list rather than stepping in and out. */}
                <span
                  className="plan-tnum"
                  style={{
                    width: 68,
                    flex: 'none',
                    paddingTop: 2,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'var(--muted)',
                  }}
                >
                  {e.allDay ? 'All day' : format(e.startAt, 'h:mm a')}
                </span>
                <span
                  aria-hidden
                  style={{
                    marginTop: 4,
                    height: 32,
                    width: 3,
                    flex: 'none',
                    borderRadius: 999,
                    background: colorOf(e),
                  }}
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span className="plan-truncate" style={{ fontSize: 14, fontWeight: 500 }}>
                    {e.title}
                  </span>
                  {e.subtitle && (
                    <span className="plan-truncate" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {e.subtitle}
                    </span>
                  )}
                  {(e.location || e.meetingUrl) && (
                    <span
                      style={{
                        marginTop: 4,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 11,
                        color: 'var(--muted)',
                      }}
                    >
                      {e.location && (
                        <span style={{ display: 'inline-flex', minWidth: 0, alignItems: 'center', gap: 4 }}>
                          <OrbitIcon name="mappin.and.ellipse" fallback={MapPin} className="h-3 w-3 shrink-0" />
                          <span className="plan-truncate">{e.location}</span>
                        </span>
                      )}
                      {e.meetingUrl && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink)' }}>
                          <OrbitIcon name="video.fill" fallback={Video} className="h-3 w-3" />
                          Join
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default AgendaView;
